/**
 * Rider Management API Hooks
 * React hooks for managing rider data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { adminApiClient } from './adminApiClient';
import { RiderDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';

/**
 * Hook to fetch a single rider by ID
 * @param id - Rider ID
 * @param options - Hook options
 */
export function useRider(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RiderDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    try {
      // First try: Fetch from /riders/{id}
      const response = await adminApiClient.get<ApiResponse<RiderDTO>>(`/riders/${id}`);

      // Handle Response wrapper - check if response has data property
      if (response && typeof response === 'object' && 'data' in response && response.data) {
        const riderData = response.data;

        // Check if essential fields are missing (KYC, Payment)
        const hasKycData = riderData.kycIdType || riderData.kycIdNumber || riderData.kycDocumentPhotoUrl;
        const hasPaymentData = riderData.preferredPaymentMethod || riderData.walletBalance !== undefined || riderData.savedPaymentMethods;

        if (!hasKycData && !hasPaymentData) {
          // Fallback: Try fetching from the list endpoint which might have more complete data
          try {
            const listResponse = await adminApiClient.get<ApiResponse<PagedData<RiderDTO>>>(
              '/riders/list',
              { page: 0, size: 1, search: riderData.phoneNumber || riderData.emailId || '' }
            );

            if (listResponse?.data?.content && listResponse.data.content.length > 0) {
              const riderFromList = listResponse.data.content.find(r => r.riderId === id);
              if (riderFromList) {
                // Merge the data, preferring the list endpoint for missing fields
                return {
                  ...riderData,
                  ...riderFromList,
                };
              }
            }
          } catch (listError) {
            // Fallback failed, continue with original data
          }
        }

        return riderData;
      }

      // If response is directly the rider object (fallback)
      return response as RiderDTO;
    } catch (error) {
      throw error;
    }
  }, [id]);

  const query = useQuery<RiderDTO>({
    queryKey: ['rider', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch paginated list of riders
 * @param params - Query parameters (page, size, sort, search, isActive). Status and search filters are server-side.
 * @param options - Hook options
 */
export function useRiders(
  params: {
    page?: number;
    size?: number;
    sort?: string[];
    search?: string;
    isActive?: boolean;
    status?: string;
  } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<RiderDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }
    if (params.status) {
      queryParams.status = params.status;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<RiderDTO>>>(
      '/riders/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    // If data is directly in response (array)
    if (Array.isArray(response)) {
      return {
        content: response,
        page: {
          size: response.length,
          number: 0,
          totalElements: response.length,
          totalPages: 1,
        },
        first: true,
        last: true,
        empty: response.length === 0,
      };
    }

    return response as any;
  }, [params.page, params.size, params.sort, params.search, params.isActive, params.status]);

  const query = useQuery<PagedData<RiderDTO>>({
    queryKey: ['riders', params],
    queryFn,
    enabled: options.enabled !== false,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return query;
}

/**
 * Hook to create a new rider
 * @param options - Hook options
 */
export function useCreateRider(
  options: {
    onSuccess?: (data: ApiResponse<RiderDTO>, variables: RiderDTO) => void;
    onError?: (error: Error, variables: RiderDTO) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<RiderDTO>, Error, RiderDTO>({
    mutationFn: async (riderData: RiderDTO) => {
      const response = await adminApiClient.post<ApiResponse<RiderDTO>>('/riders', riderData);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing rider
 * @param options - Hook options
 */
export function useUpdateRider(
  options: {
    onSuccess?: (
      data: ApiResponse<RiderDTO>,
      variables: { id: number; data: RiderDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: RiderDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<RiderDTO>, Error, { id: number; data: RiderDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: RiderDTO }) => {
      const response = await adminApiClient.put<ApiResponse<RiderDTO>>(`/riders/${id}`, data);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a rider
 * @param options - Hook options
 */
export function useDeleteRider(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: DeleteRiderVariables) => void;
    onError?: (error: Error, variables: DeleteRiderVariables) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<void>, Error, DeleteRiderVariables>({
    mutationFn: async (variables: DeleteRiderVariables) => {
      // First delete the rider
      const response = await adminApiClient.delete<ApiResponse<void>>(`/riders/${variables.riderId}`);

      // Then find and delete the corresponding user if email is provided
      if (variables.email) {
        try {
          const userResponse = await adminApiClient.get<any>(`/api/admin/user-by-email?email=${encodeURIComponent(variables.email)}`);
          if (userResponse.data?.id) {
            await adminApiClient.delete<ApiResponse<void>>(`/api/admin/${userResponse.data.id}`);
          }
        } catch (userError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding user:', userError);
        }
      }

      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.riderId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Variables for delete rider operation
 */
export interface DeleteRiderVariables {
  riderId: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Hook to toggle rider active status
 * @param options - Hook options
 */
export function useToggleRiderActiveStatus(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<void>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await adminApiClient.get<ApiResponse<void>>(`/riders/toggle-active-status/${id}`);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables] });

      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to upload rider profile photo (multipart)
 * @param options - Hook options
 */
/**
 * Hook to upload rider KYC documents (multipart)
 * POST /riders/upload-kyc-documents?govtIdType=...&govtIdNumber=...&riderId=...
 */
export function useUploadRiderKycDocuments(
  options: {
    onSuccess?: (data: ApiResponse<RiderDTO>, variables: { riderId: number; govtIdType: string; govtIdNumber: string; file: File }) => void;
    onError?: (error: Error, variables: { riderId: number; govtIdType: string; govtIdNumber: string; file: File }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<RiderDTO>, Error, { riderId: number; govtIdType: string; govtIdNumber: string; file: File }>({
    mutationFn: async ({ riderId, govtIdType, govtIdNumber, file }) => {
      const formData = new FormData();
      formData.append('kycDocumentPhotoUrl', file);

      const response = await adminApiClient.post<ApiResponse<RiderDTO>>(
        '/riders/upload-kyc-documents',
        formData,
        { params: { govtIdType, govtIdNumber, riderId } }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.riderId] });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}

/**
 * Hook to upload rider profile photo (multipart)
 * PUT /riders/upload-profile-photo/{riderId}
 */
export function useUploadRiderProfilePhoto(
  options: {
    onSuccess?: (data: ApiResponse<RiderDTO>, variables: { riderId: number; file: File }) => void;
    onError?: (error: Error, variables: { riderId: number; file: File }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<RiderDTO>, Error, { riderId: number; file: File }>({
    mutationFn: async ({ riderId, file }) => {
      const formData = new FormData();
      formData.append('profilePhotoUrl', file);

      const response = await adminApiClient.put<ApiResponse<RiderDTO>>(
        `/riders/upload-profile-photo/${riderId}`,
        formData
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.riderId] });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}
